import React, { useState } from 'react';
import { Upload, Camera, X, Loader } from 'lucide-react';
import { extractReceiptData, fileToDataUrl } from '../../services/ocrService';

const ReceiptUpload = ({ onReceiptProcessed }) => {
    const [uploading, setUploading] = useState(false);
    const [ocrProgress, setOcrProgress] = useState(0);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        await processFile(file);
    };

    const processFile = async (file) => {
        // Check if file is a PDF
        const isPDF = file.type === 'application/pdf';

        try {
            setError(null);
            setUploading(true);
            setOcrProgress(0);

            // Convert to data URL for preview and storage
            const dataUrl = await fileToDataUrl(file);
            setPreview(dataUrl);

            if (isPDF) {
                // For PDFs, convert first page to image then run OCR
                setOcrProgress(10);

                // Convert PDF to image
                const imageDataUrl = await convertPdfToImage(file);

                setOcrProgress(30);

                // Run OCR on the converted image
                const response = await fetch(imageDataUrl);
                const blob = await response.blob();
                const imageFile = new File([blob], 'receipt.png', { type: 'image/png' });

                const ocrData = await extractReceiptData(imageFile, (progress) => {
                    // Map progress from 30-100
                    setOcrProgress(30 + (progress * 0.7));
                });

                // Pass data back to parent with original PDF
                onReceiptProcessed({
                    ...ocrData,
                    receiptUrl: dataUrl  // Keep original PDF
                });
            } else {
                // Extract data using OCR for images
                const ocrData = await extractReceiptData(file, (progress) => {
                    setOcrProgress(progress);
                });

                // Pass data back to parent
                onReceiptProcessed({
                    ...ocrData,
                    receiptUrl: dataUrl
                });
            }

            setUploading(false);
        } catch (err) {
            console.error('Error processing receipt:', err);
            setError(isPDF ? 'Failed to process PDF. Please try again.' : 'Failed to process receipt. Please try again.');
            setUploading(false);
        }
    };

    // Convert PDF first page to image for OCR
    const convertPdfToImage = async (file) => {
        return new Promise(async (resolve, reject) => {
            try {
                const pdfjsLib = await import('pdfjs-dist');

                // Use matching worker version 5.4.449
                pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs';

                const arrayBuffer = await file.arrayBuffer();
                const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                const page = await pdf.getPage(1);

                const viewport = page.getViewport({ scale: 2.0 });
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                await page.render({
                    canvasContext: context,
                    viewport: viewport
                }).promise;

                resolve(canvas.toDataURL('image/png'));
            } catch (error) {
                reject(error);
            }
        });
    };

    const clearReceipt = () => {
        setPreview(null);
        setError(null);
        setOcrProgress(0);
    };

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Receipt
            </label>

            {!preview ? (
                <div style={{
                    border: '2px dashed var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '2rem',
                    textAlign: 'center',
                    background: 'var(--card-bg)'
                }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <Upload size={48} style={{ color: 'var(--text-muted)' }} />
                    </div>

                    <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
                        Upload or capture receipt
                    </p>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            <Upload size={16} style={{ marginRight: '0.5rem' }} />
                            Upload File
                            <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </label>

                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            <Camera size={16} style={{ marginRight: '0.5rem' }} />
                            Take Photo
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </label>
                    </div>
                </div>
            ) : (
                <div style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '1rem',
                    background: 'var(--card-bg)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <span style={{ fontWeight: 600 }}>Receipt Preview</span>
                        <button
                            onClick={clearReceipt}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--text-muted)',
                                padding: '0.25rem'
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {preview?.startsWith('data:application/pdf') ? (
                        <iframe
                            src={preview}
                            title="Receipt PDF Preview"
                            style={{
                                width: '100%',
                                height: '600px',
                                border: 'none',
                                borderRadius: 'var(--radius)'
                            }}
                        />
                    ) : (
                        <img
                            src={preview}
                            alt="Receipt preview"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '600px',
                                objectFit: 'contain',
                                borderRadius: 'var(--radius)'
                            }}
                        />
                    )}

                    {uploading && (
                        <div style={{ marginTop: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <Loader size={16} className="spin" />
                                <span>
                                    {preview?.startsWith('data:application/pdf')
                                        ? `Processing PDF... ${Math.round(ocrProgress)}%`
                                        : `Processing receipt... ${Math.round(ocrProgress)}%`}
                                </span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '4px',
                                background: 'var(--border)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${ocrProgress}%`,
                                    height: '100%',
                                    background: 'var(--primary)',
                                    transition: 'width 0.3s'
                                }} />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div style={{
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: 'var(--radius)',
                    color: '#c00'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default ReceiptUpload;
